/*
 * Copyright (c) 2024, Groupe Dynamite Inc.
 * All rights reserved.
 * Custom hook for gift card functionality on PDP
 */

import {useRef, useMemo, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useHistory, useLocation} from 'react-router-dom'
import useCheckBalanceDrawer from './use-check-balance-drawer'

/**
 * Hook to manage gift card state and logic for ProductView
 * Extracts gift card specific functionality to keep ProductView cleaner
 *
 * @param {Object} product - The product object
 * @param {Object} options - Additional options
 * @param {Object} options.variant - The selected variant
 * @param {Array} options.variationAttributes - Product variation attributes
 * @param {boolean} options.isOutOfStock - Whether product is out of stock
 * @param {boolean} options.showInventoryMessage - Whether to show inventory message
 * @returns {Object} Gift card state and functions
 */
const useGiftCard = (product, options = {}) => {
    const {variant, variationAttributes, isOutOfStock, showInventoryMessage} = options
    const intl = useIntl()
    const history = useHistory()
    const location = useLocation()
    const giftCardFormRef = useRef(null)

    // Determine if this is a gift card page
    const isGiftPage = product?.c_isGiftCard || product?.c_isEGiftCard

    // Auto-select first color variant for gift cards when on master product
    // This ensures the gift card images display correctly on initial load
    // We navigate to the color variation group URL (e.g., /p/e-gift-card/GARGIFTCARDGBEGC002.html?color=2)
    useEffect(() => {
        if (!product || !isGiftPage) return

        // Check if we're on a master product (has variants and is master type)
        const isMasterProduct = product.variants?.length > 0 && product.type?.master

        if (!isMasterProduct) return

        // Find the first color variation group
        const colorAttr = product.variationAttributes?.find((attr) => attr.id === 'color')
        const firstColorValue = colorAttr?.values?.[0]?.value

        // Find the variation group for this color (not the full variant)
        const firstColorGroup = product.variationGroups?.find((group) =>
            group.variationValues?.color === firstColorValue
        )
    }, [product?.id, isGiftPage]) // Only run when product ID changes

    // Check balance drawer state
    const {
        isOpen: isCheckBalanceOpen,
        onOpen: handleCheckBalanceOpen,
        onClose: handleCheckBalanceClose
    } = useCheckBalanceDrawer(isGiftPage)

    /**
     * Calculate gift card specific orderable state
     * Returns whether the add to cart button should be disabled for gift cards
     */
    const giftCardOrderableState = useMemo(() => {
        if (!isGiftPage) {
            return {isGiftCardDisabled: false, reason: null}
        }

        const hasUnselectedVariations = variationAttributes?.some(
            (attr) => !attr.selectedValue?.value
        )

        const allVariationsSelected =
            variationAttributes?.length > 0 &&
            variationAttributes.every((attr) => attr.selectedValue?.value)

        // If variations are not fully selected, disable the button
        if (hasUnselectedVariations) {
            return {isGiftCardDisabled: true, reason: 'unselected_variations'}
        }

        // If all variations are selected but no variant exists, it's unavailable
        if (allVariationsSelected && !variant) {
            return {isGiftCardDisabled: true, reason: 'unavailable'}
        }

        // If variant exists but is not orderable
        if (variant && !variant.orderable) {
            return {isGiftCardDisabled: true, reason: 'not_orderable'}
        }

        // If variant exists and IS orderable - enable the button
        if (variant && variant.orderable) {
            return {isGiftCardDisabled: false, reason: null}
        }

        // Use isOutOfStock from the hook as fallback
        if (isOutOfStock) {
            return {isGiftCardDisabled: true, reason: 'out_of_stock'}
        }

        return {isGiftCardDisabled: showInventoryMessage || isOutOfStock, reason: null}
    }, [isGiftPage, variant, variationAttributes, isOutOfStock, showInventoryMessage])

    /**
     * Validate the gift card form
     * @returns {Promise<boolean>} Whether the form is valid
     */
    const validateGiftCardForm = async () => {
        if (!product?.c_isEGiftCard || !giftCardFormRef.current) {
            return true
        }
        return await giftCardFormRef.current.validate()
    }

    /**
     * Get gift card data from form for add to cart
     * @returns {Object|null} Gift card data or null if not applicable
     */
    const getGiftCardData = () => {
        if (!product?.c_isEGiftCard || !giftCardFormRef.current) {
            return null
        }

        const formValues = giftCardFormRef.current.getValues()
        return {
            c_giftCardFrom: formValues.yourName,
            c_giftCardTo: formValues.recipientName,
            c_giftCardToEmail: formValues.recipientEmail,
            c_giftCardMessage: formValues.message || '',
            c_isGiftCard: true
        }
    }

    /**
     * Reset the gift card form after successful add to cart
     */
    const resetGiftCardForm = () => {
        if (product?.c_isEGiftCard && giftCardFormRef.current) {
            giftCardFormRef.current.reset()
        }
    }

    /**
     * Check if the gift card form has errors
     * @returns {boolean} Whether the form has errors
     */
    const hasGiftCardFormErrors = () => {
        if (!product?.c_isEGiftCard || !giftCardFormRef.current) {
            return false
        }
        const formState = giftCardFormRef.current.getFormState()
        return Object.keys(formState.errors).length > 0
    }

    /**
     * Get error message for missing attributes including gift card form errors
     * @param {Array} variationAttributes - Product variation attributes
     * @returns {string|null} Error message or null
     */
    const getGiftCardErrorMessage = (variationAttributes) => {
        const missingAttributes = (variationAttributes || [])
            .filter((attr) => !attr.selectedValue?.value)
            .map((attr) => attr.name)

        const hasGiftErrors = hasGiftCardFormErrors()

        // If only gift card errors, show specific message
        if (missingAttributes.length === 0 && hasGiftErrors) {
            return intl.formatMessage({
                defaultMessage: 'Please complete all required gift card information',
                id: 'product_view.error.gift_card_required'
            })
        }

        // If both missing attributes and gift card errors
        if (missingAttributes.length > 0 && hasGiftErrors) {
            const attributeMessage =
                missingAttributes.length === 1
                    ? `Please select a ${missingAttributes[0].toLowerCase()}`
                    : `Please select ${missingAttributes.join(', ').toLowerCase()}`

            return intl.formatMessage(
                {
                    defaultMessage: '{attributes} and complete the gift card information',
                    id: 'product_view.error.attributes_and_gift_card'
                },
                {attributes: attributeMessage}
            )
        }

        return null
    }

    return {
        // State
        isGiftPage,
        giftCardFormRef,
        giftCardOrderableState,

        // Check balance drawer
        isCheckBalanceOpen,
        handleCheckBalanceOpen,
        handleCheckBalanceClose,

        // Functions
        validateGiftCardForm,
        getGiftCardData,
        resetGiftCardForm,
        hasGiftCardFormErrors,
        getGiftCardErrorMessage
    }
}

export default useGiftCard
